import { forNamespace } from 'common/lib/util/i18n';
const l = forNamespace('home card overflow menu title');

export type FeedType = 'UP_NEXT' | 'HIGHLIGHTS' | 'DUE_DATES';

export const UP_NEXT_TYPE: FeedType = 'UP_NEXT';
export const HIGHLIGHT_TYPE: FeedType = 'HIGHLIGHTS';
export const DUE_DATE_TYPE: FeedType = 'DUE_DATES';

export type ItemKey =
  | 'subscribe'
  | 'unsubscribe'
  | 'addDueDate'
  | 'changeDueDate'
  | 'addMembers'
  | 'joinCard'
  | 'leaveCard';

type ItemKeys = { [key in ItemKey]?: boolean };

export interface CardInfo {
  editable: boolean;
  hasDueDate: boolean;
  hasMeAsMember: boolean;
  cardId: string;
  isSubscribed: boolean;
}

export type Handlers = { [key in ItemKey]: () => void };

export interface OverflowItem {
  key: ItemKey;
  name: string;
  onClick: () => void;
  shouldShow: boolean;
}

interface Tracking {
  // TMON-962: Calculate `prepObj` and `indObj` here instead of in
  // `up-next.coffee`.
  prepObj: string;
  indObj: string;
  trackUe: (
    category: string,
    verb: string,
    dirObj: string,
    prepObj: string,
    indObj: string,
    method: string,
    context: object,
  ) => void;
}

const getTrackingMethod = (feedType: FeedType, itemName: string): string => {
  /* tslint:disable:switch-default */
  switch (feedType) {
    case 'UP_NEXT':
      return `by clicking ${itemName} menu item on up next card`;
    case 'HIGHLIGHTS':
      return `by clicking ${itemName} menu item on highlights card`;
    case 'DUE_DATES':
      return `by clicking ${itemName} menu item on due dates card`;
  }
  /* tslint:enable:switch-default */
};

const trackSubscribe = (
  feedType: FeedType,
  tracking: Tracking,
  cardId: string,
) => {
  const { trackUe, prepObj = '' } = tracking;

  const method = getTrackingMethod(feedType, 'subscribe');
  const context = { cardId };
  trackUe('home', 'subscribes', 'card', prepObj, '', method, context);
};

const trackUnsubscribe = (
  feedType: FeedType,
  tracking: Tracking,
  cardId: string,
) => {
  const { trackUe, prepObj } = tracking;

  const method = getTrackingMethod(feedType, 'unsubscribe');
  const context = { cardId };
  trackUe('home', 'unsubscribes', 'card', prepObj, '', method, context);
};

const trackAddDueDate = (
  feedType: FeedType,
  tracking: Tracking,
  cardId: string,
) => {
  const { trackUe, indObj } = tracking;

  const method = getTrackingMethod(feedType, 'due date');
  const context = { cardId };
  trackUe('home', 'adds', 'due date', '', indObj, method, context);
};

const trackChangeDueDate = (
  feedType: FeedType,
  tracking: Tracking,
  cardId: string,
) => {
  const { trackUe, indObj } = tracking;

  const method = getTrackingMethod(feedType, 'due date');
  const context = { cardId };
  trackUe('home', 'changes', 'due date', '', indObj, method, context);
};

const trackAddMembers = (
  feedType: FeedType,
  tracking: Tracking,
  cardId: string,
) => {
  const { trackUe, indObj } = tracking;

  const method = getTrackingMethod(feedType, 'members');
  const context = { cardId };
  trackUe('home', 'changes', 'members', '', indObj, method, context);
};

const trackJoinCard = (
  feedType: FeedType,
  tracking: Tracking,
  cardId: string,
) => {
  const { trackUe, indObj } = tracking;

  const method = getTrackingMethod(feedType, 'join card');
  const context = { cardId };
  trackUe('home', 'joins', 'card', '', indObj, method, context);
};

const trackLeaveCard = (
  feedType: FeedType,
  tracking: Tracking,
  cardId: string,
) => {
  const { trackUe, indObj } = tracking;

  const method = getTrackingMethod(feedType, 'leave card');
  const context = { cardId };
  trackUe('home', 'leaves', 'card', '', indObj, method, context);
};

export const getAllOverflowItems = (
  feedType: FeedType,
  cardInfo: CardInfo,
  handlers: Handlers,
  tracking: Tracking,
): OverflowItem[] => {
  const {
    isSubscribed,
    hasDueDate,
    editable,
    hasMeAsMember,
    cardId,
  } = cardInfo;

  const items: OverflowItem[] = [
    {
      key: 'subscribe',
      name: l('watch'),
      shouldShow: !isSubscribed,
      onClick: () => {
        trackSubscribe(feedType, tracking, cardId);
        handlers.subscribe();
      },
    },
    {
      key: 'unsubscribe',
      name: l('stop watching'),
      shouldShow: isSubscribed,
      onClick: () => {
        trackUnsubscribe(feedType, tracking, cardId);
        handlers.unsubscribe();
      },
    },
    {
      key: 'addDueDate',
      name: l('add due date'),
      shouldShow: !hasDueDate && editable,
      onClick: () => {
        trackAddDueDate(feedType, tracking, cardId);
        handlers.addDueDate();
      },
    },
    {
      key: 'changeDueDate',
      name: l('change due date'),
      shouldShow: hasDueDate && editable,
      onClick: () => {
        trackChangeDueDate(feedType, tracking, cardId);
        handlers.changeDueDate();
      },
    },
    {
      key: 'addMembers',
      name: l('add members to card'),
      shouldShow: editable,
      onClick: () => {
        trackAddMembers(feedType, tracking, cardId);
        handlers.addMembers();
      },
    },
    {
      key: 'joinCard',
      name: l('join card'),
      shouldShow: !hasMeAsMember && editable,
      onClick: () => {
        trackJoinCard(feedType, tracking, cardId);
        handlers.joinCard();
      },
    },
    {
      key: 'leaveCard',
      name: l('leave card'),
      shouldShow: hasMeAsMember && editable,
      onClick: () => {
        trackLeaveCard(feedType, tracking, cardId);
        handlers.leaveCard();
      },
    },
  ];

  return items;
};

const getItemKeysByFeedType = (feedType: FeedType): ItemKeys => {
  /* tslint:disable:switch-default */
  switch (feedType) {
    case 'UP_NEXT':
      return {
        subscribe: true,
        unsubscribe: true,
        addDueDate: true,
        changeDueDate: true,
        addMembers: true,
      };
    case 'HIGHLIGHTS':
      return {
        subscribe: true,
        unsubscribe: true,
        joinCard: true,
        leaveCard: true,
      };
    case 'DUE_DATES':
      return {
        subscribe: true,
        unsubscribe: true,
        addMembers: true,
      };
  }
  /* tslint:enable:switch-default */
};

export const getOverflowItemsByFeedType = (
  feedType: FeedType,
  cardInfo: CardInfo,
  handlers: Handlers,
  tracking: Tracking,
): OverflowItem[] => {
  const itemKeys = getItemKeysByFeedType(feedType);

  const overflowItems = getAllOverflowItems(
    feedType,
    cardInfo,
    handlers,
    tracking,
  );

  return overflowItems.filter((item) => item.shouldShow && itemKeys[item.key]);
};



// WEBPACK FOOTER //
// ./app/scripts/views/home/presentational/util/overflow-menu-items.ts